import React, { ReactNode } from "react";
import { LinearProgress, Typography } from "@mui/material";
import { Inline, Stack } from "components/layout/reusableLayouts";

interface CardLimitRowProps {
  label: ReactNode;
  value: ReactNode;
  /**
   * A pending replacement: the current value strikes through and this renders
   * beneath it, the same way Summary shows one.
   */
  pendingValue?: ReactNode;
  /**
   * Share of the limit already spent, 0 to 100. Omit for no bar: a
   * transaction limit caps a single purchase, so nothing accumulates against
   * it and there is no proportion to draw.
   */
  spentPercent?: number;
}

/**
 * A spend control: label left, amount right, and the bar underneath when the
 * limit is one that accumulates.
 *
 * Not the Summary field list, which puts the label above the value: these read
 * as a meter with a caption rather than as a field, and the bar needs the full
 * width that a half-column field does not have.
 */
const CardLimitRow: React.FC<CardLimitRowProps> = ({
  label,
  value,
  pendingValue,
  spentPercent,
}) => (
  <Stack gap="var(--space-2)" sx={{ paddingBlock: "var(--space-3)" }}>
    <Inline justify="between" align="start" gap="var(--space-4)" wrap={false}>
      <Typography variant="body1" sx={{ color: "var(--color-text-secondary)" }}>
        {label}
      </Typography>
      <Stack gap="0" sx={{ alignItems: "flex-end", minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            textAlign: "right",
            textDecoration: pendingValue ? "line-through" : undefined,
            color: pendingValue
              ? "var(--color-text-tertiary)"
              : "var(--color-text-primary)",
          }}
        >
          {value}
        </Typography>
        {pendingValue && (
          <Typography variant="body1" sx={{ textAlign: "right" }}>
            {pendingValue}
          </Typography>
        )}
      </Stack>
    </Inline>
    {spentPercent !== undefined && (
      <LinearProgress variant="determinate" value={spentPercent} />
    )}
  </Stack>
);

export default CardLimitRow;
